// UserRoles.jsx
import React from 'react';
import { 
  Box, Typography, Dialog, DialogTitle, DialogContent, 
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Card, CardContent, CardActions, Grid, 
  Avatar, Chip, Button, TextField, InputAdornment, IconButton
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonIcon from '@mui/icons-material/Person';
import { X } from 'lucide-react';
import { useUserRoles } from '../../hooks/userRolesHook';
import styles from '../../styles/purchaseInvoices/pages/userRolesStyles';

const UserRoles = () => {
  const {
    dialogOpen,
    selectedUser,
    roleValue,
    searchTerm,
    users,
    isLoading,
    updateMutation,
    setSearchTerm,
    setRoleValue,
    handleDialogOpen,
    handleDialogClose,
    handleSubmit,
    getRoleDisplay,
    groups, 
    setSelectedUser, 
  } = useUserRoles();

  const [groupDialogOpen, setGroupDialogOpen] = React.useState(false);
  const [groupUser, setGroupUser] = React.useState(null);
  const [groupValue, setGroupValue] = React.useState('');

  const handleGroupDialogOpen = (user) => {
    setGroupUser(user);
    setGroupValue(user.groupId || '');
    setGroupDialogOpen(true);
  };

  const handleGroupDialogClose = () => {
    setGroupDialogOpen(false);
    setGroupUser(null);
  };

  const handleGroupSubmit = () => {
    updateMutation.mutate(
      { userId: groupUser._id, role: groupUser.invoiceRole, groupId: groupValue },
      {
        onSuccess: () => {
          setGroupDialogOpen(false);
        }
      }
    );
  };

  const selectableGroups = groups.filter(g => !g.isParentGroup);

  if (isLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" color="textSecondary" sx={styles.loadingText}>
          טוען נתונים...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerBox}>
        <Box sx={styles.searchContainer}>
          <TextField
            placeholder="חיפוש משתמש..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'primary.main' }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ dir: "rtl" }}
            sx={styles.searchInput}
          />
        </Box>
      </Box>

      {users.length > 0 ? (
        <Grid container spacing={3} sx={styles.gridContainer}>
          {users.map((user) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              lg={3} 
              key={user._id}
            >
              <Card sx={styles.card}>
                <Box sx={styles.cardHeader}>
                  <Box sx={styles.cardHeaderContent}>
                    <Avatar sx={styles.userAvatar}>
                      {user.Name?.charAt(0) || "U"}
                    </Avatar>
                    <Typography variant="h6" sx={styles.userName} title={`${user.Name} ${user.LastName}`}>
                      {user.Name} {user.LastName}
                    </Typography>
                  </Box>
                </Box>
                
                <CardContent sx={styles.cardContent}>
                  <Box sx={styles.infoRow} title={user.Email}>
                    <EmailIcon sx={styles.infoIcon} />
                    <Typography variant="body2" sx={styles.infoText}>
                      {user.Email}
                    </Typography>
                  </Box>
                  
                  <Box sx={styles.infoRow}>
                    <BusinessIcon sx={styles.infoIcon} />
                    <Typography variant="body2" sx={styles.infoText} title={user.Branches?.join(', ') || 'כל הסניפים'}>
                      {user.Branches?.join(', ') || 'כל הסניפים'}
                    </Typography>
                  </Box>
                  
                  <Box sx={styles.infoRow}>
                    <GroupsIcon sx={styles.infoIcon} />
                    <Typography variant="body2" sx={styles.infoText}>
                      <Box component="span" sx={{ fontWeight: 600 }}>קבוצה:</Box>{' '}
                      {user.groupName || 'ללא קבוצה'}
                    </Typography>
                    <IconButton 
                      size="small" 
                      sx={styles.editIconButton}
                      onClick={() => handleGroupDialogOpen(user)}
                      aria-label="שנה קבוצה"
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Box sx={styles.roleChipContainer}>
                    <BadgeIcon sx={styles.infoIcon} />
                    <Chip 
                      label={getRoleDisplay(user.invoiceRole)} 
                      size="small" 
                      sx={styles.roleChip(user.invoiceRole)}
                    />
                  </Box>
                </CardContent>
                
                <CardActions sx={styles.cardActions}>
                  <Button 
                    variant="contained" 
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleDialogOpen(user)}
                    sx={styles.editButton}
                    fullWidth
                  >
                    שנה תפקיד
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={styles.emptyBox}>
          <PersonIcon sx={styles.emptyIcon} />
          <Typography variant="h6" sx={styles.emptyTitle}>
            לא נמצאו משתמשים
          </Typography>
          <Typography variant="body2" sx={styles.emptySubtitle}>
            {searchTerm ? 'נסה לשנות את מונחי החיפוש' : 'אין משתמשים במערכת'}
          </Typography>
        </Box>
      )}

      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose} 
        dir="rtl"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.dialog }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          <Typography sx={styles.dialogTitleText}>
            עדכון תפקיד משתמש
          </Typography>
          
          <IconButton
            onClick={handleDialogClose}
            sx={styles.dialogCloseButton}
            size="small"
          >
            <X size={20} strokeWidth={2.5} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          <Box sx={styles.userInfoBox}>
            <Box sx={styles.userInfoRow}>
              <Typography variant="body1" sx={styles.userInfoLabel}>
                שם משתמש:
              </Typography>
              <Typography variant="body1" sx={styles.userInfoValue}>
                {selectedUser?.Name} {selectedUser?.LastName}
              </Typography>
            </Box>
            <Box sx={styles.userInfoRow}>
              <Typography variant="body1" sx={styles.userInfoLabel}>
                דוא"ל:
              </Typography>
              <Typography variant="body1" sx={styles.userInfoValue}>
                {selectedUser?.Email}
              </Typography>
            </Box>
          </Box>
          
          <FormControl fullWidth sx={styles.dialogForm}>
            <InputLabel id="role-label">תפקיד</InputLabel>
            <Select
              labelId="role-label"
              value={roleValue}
              label="תפקיד"
              onChange={(e) => setRoleValue(e.target.value)}
            >
              <MenuItem value="regular">ברירת מחדל</MenuItem>
              <MenuItem value="approver">מאשר</MenuItem>
              <MenuItem value="accountManager">מנהל/ת חשבונות</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button 
            onClick={handleDialogClose}
            sx={styles.cancelButton}
          >
            ביטול
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={updateMutation.isPending}
            sx={styles.submitButton}
          >
            {updateMutation.isPending ? <CircularProgress size={24} /> : "שמור"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={groupDialogOpen} 
        onClose={handleGroupDialogClose} 
        dir="rtl"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.dialog }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          <Typography sx={styles.dialogTitleText}>
            שנה קבוצה
          </Typography>
          
          <IconButton
            onClick={handleGroupDialogClose}
            sx={styles.dialogCloseButton}
            size="small"
          >
            <X size={20} strokeWidth={2.5} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          <FormControl fullWidth variant="outlined" size="small" sx={styles.dialogForm}>
            <InputLabel id="group-label">בחר קבוצה</InputLabel>
            <Select
              labelId="group-label"
              value={groupValue}
              label="בחר קבוצה"
              onChange={e => setGroupValue(e.target.value)}
            >
              <MenuItem value="">ללא קבוצה</MenuItem>
              {(() => {
                const cur = groups.find(g => g._id === groupValue);
                return cur?.isParentGroup ? (
                  <MenuItem key={cur._id} value={cur._id} disabled>
                    {cur.name} (קבוצת אב — לא ניתנת לשיוך)
                  </MenuItem>
                ) : null;
              })()}
              {selectableGroups.map(group => (
                <MenuItem key={group._id} value={group._id}>{group.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button 
            onClick={handleGroupDialogClose}
            sx={styles.cancelButton}
          >
            ביטול
          </Button>
          <Button 
            onClick={handleGroupSubmit} 
            variant="contained"
            disabled={updateMutation.isPending}
            sx={styles.submitButton}
          >
            {updateMutation.isPending ? <CircularProgress size={24} /> : "שמור"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRoles;